import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Save, X, FolderOpen } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { invalidateCategoriesCache } from '../lib/categories';
import { notifyCategoriesChanged } from '../hooks/useCategories';
import TranslateButton from './TranslateButton';

const ICONS = ['📚', '🔧', '📊', '🖥️', '⚡', '🤖', '🌐', '🎯', '⚙️', '🛠️', '💡', '🔬', '🎓', '📈', '🚀', '💾', '🔌', '🏭'];

const CategoriesManager = () => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category being edited
  const [form, setForm] = useState({ nameAr: '', nameEn: '', icon: '📚', order: 0 });
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/categories');
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error(L('فشل في تحميل التصنيفات', 'Failed to load categories'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refreshOthers = () => { invalidateCategoriesCache(); notifyCategoriesChanged(); };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.nameAr.trim()) return;
    try {
      await api.post('/categories', { nameAr: form.nameAr.trim(), nameEn: form.nameEn.trim(), icon: form.icon, order: form.order });
      toast.success(L('تم إضافة التصنيف', 'Category added'));
      setForm({ nameAr: '', nameEn: '', icon: '📚', order: 0 });
      setShowAdd(false);
      await load();
      refreshOthers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ nameAr: c.nameAr, nameEn: c.nameEn, icon: c.icon || '📚', order: c.order || 0 });
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/categories/${id}`, { nameAr: form.nameAr, nameEn: form.nameEn, icon: form.icon, order: form.order });
      toast.success(L('تم الحفظ', 'Saved'));
      setEditing(null);
      await load();
      refreshOthers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const remove = async (c) => {
    if (c.slug === 'general') {
      toast.error(L('لا يمكن حذف التصنيف الافتراضي "عام"', 'Cannot delete the default "general" category'));
      return;
    }
    if (!window.confirm(L(`حذف "${c.nameAr}"؟ سيتم نقل المحتوى إلى "عام".`, `Delete "${c.nameEn}"? Content will be reassigned to "General".`))) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success(L('تم الحذف', 'Deleted'));
      await load();
      refreshOthers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="text-center py-10 text-slate-500">{L('جارٍ التحميل...', 'Loading...')}</div>;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="flex items-center gap-2 text-slate-900">
          <FolderOpen className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-lg">{L('إدارة التصنيفات', 'Manage Categories')}</h3>
          <span className="text-sm text-slate-500">({items.length})</span>
        </div>
        {!showAdd && (
          <Button onClick={() => { setShowAdd(true); setForm({ nameAr: '', nameEn: '', icon: '📚', order: items.length + 1 }); }} className="btn-primary-blue border-0" data-testid="add-category-btn">
            <Plus className="w-4 h-4 me-2" /> {L('إضافة تصنيف', 'Add Category')}
          </Button>
        )}
      </div>

      {showAdd && (
        <form onSubmit={create} className="rounded-2xl bg-white border-2 border-blue-200 p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="cat-name-ar" required value={form.nameAr} onChange={e => setForm({ ...form, nameAr: e.target.value })} placeholder={L('الاسم بالعربية', 'Name (Arabic)')} className="bg-slate-50 border-slate-200 text-slate-900" />
            <Input data-testid="cat-name-en" value={form.nameEn} onChange={e => setForm({ ...form, nameEn: e.target.value })} placeholder={L('الاسم بالإنجليزية', 'Name (English)')} className="bg-slate-50 border-slate-200 text-slate-900" dir="ltr" />
          </div>
          <div className="flex flex-wrap gap-2">
            <TranslateButton
              sourceLang="ar"
              targetLang="en"
              getValues={() => form}
              setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
              fields={[{ source: 'nameAr', target: 'nameEn' }]}
              testId="cat-translate-ar-en"
            />
            <TranslateButton
              sourceLang="en"
              targetLang="ar"
              getValues={() => form}
              setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
              fields={[{ source: 'nameEn', target: 'nameAr' }]}
              testId="cat-translate-en-ar"
            />
          </div>
          <div>
            <div className="text-slate-600 text-xs mb-2">{L('الأيقونة', 'Icon')}</div>
            <div className="flex flex-wrap gap-2">
              {ICONS.map(ic => (
                <button key={ic} type="button" onClick={() => setForm({ ...form, icon: ic })} className={`w-10 h-10 rounded-lg border text-xl flex items-center justify-center transition-all ${form.icon === ic ? 'bg-blue-600 border-blue-400 scale-110' : 'bg-slate-50 border-slate-200 hover:border-blue-500'}`}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="btn-primary-blue border-0" data-testid="save-new-cat-btn"><Save className="w-4 h-4 me-2" />{L('حفظ', 'Save')}</Button>
            <Button type="button" onClick={() => setShowAdd(false)} variant="outline" className="border-blue-200 text-slate-700">{L('إلغاء', 'Cancel')}</Button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map(c => (
          <div key={c.id} data-testid={`cat-row-${c.slug}`} className="rounded-xl bg-white border border-slate-200 p-4">
            {editing === c.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.nameAr} onChange={e => setForm({ ...form, nameAr: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 text-sm" />
                  <Input value={form.nameEn} onChange={e => setForm({ ...form, nameEn: e.target.value })} className="bg-slate-50 border-slate-200 text-slate-900 text-sm" dir="ltr" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <TranslateButton
                    sourceLang="ar"
                    targetLang="en"
                    getValues={() => form}
                    setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
                    fields={[{ source: 'nameAr', target: 'nameEn' }]}
                    testId={`edit-cat-translate-ar-en-${c.slug}`}
                  />
                  <TranslateButton
                    sourceLang="en"
                    targetLang="ar"
                    getValues={() => form}
                    setValue={(k, v) => setForm(prev => ({ ...prev, [k]: v }))}
                    fields={[{ source: 'nameEn', target: 'nameAr' }]}
                    testId={`edit-cat-translate-en-ar-${c.slug}`}
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setForm({ ...form, icon: ic })} className={`w-8 h-8 rounded text-lg flex items-center justify-center ${form.icon === ic ? 'bg-blue-600' : 'bg-slate-50 hover:bg-slate-100'}`}>
                      {ic}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => saveEdit(c.id)} size="sm" className="btn-primary-blue border-0"><Save className="w-3 h-3 me-1" />{L('حفظ', 'Save')}</Button>
                  <Button onClick={() => setEditing(null)} size="sm" variant="outline" className="border-blue-200 text-slate-700"><X className="w-3 h-3 me-1" />{L('إلغاء', 'Cancel')}</Button>
                </div>
              </div>
            ) : (
              <div className={`flex items-center justify-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-2xl shrink-0">{c.icon || '📚'}</div>
                  <div className="min-w-0">
                    <div className="text-slate-900 font-bold truncate">{c.nameAr}</div>
                    <div className="text-slate-500 text-xs truncate" dir="ltr">{c.nameEn}</div>
                    <div className="text-slate-500 text-[11px] mt-0.5" dir="ltr">slug: {c.slug}</div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(c)} data-testid={`edit-cat-${c.slug}`} className="w-8 h-8 rounded-lg bg-blue-100 hover:bg-blue-500 text-blue-700 hover:text-white flex items-center justify-center transition-colors"><Pencil className="w-4 h-4" /></button>
                  {c.slug !== 'general' && (
                    <button onClick={() => remove(c)} data-testid={`delete-cat-${c.slug}`} className="w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500 text-red-700 hover:text-white flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoriesManager;
